/**
 * Shared cart-view line-filtering logic. Same predicate used by:
 *   - AssignmentBoard's sticky toolbar (what to render)
 *   - SuggestionDetailModal's prev/next sibling navigation (what to walk)
 *
 * Keeping the function pure + the inputs serializable (chips/search live in
 * the URL) means buyer expectations stay aligned: hitting Next while a `<MOV`
 * chip is active skips past lines you can't see.
 */

import type {
  AssignmentLine,
  CartAssignment,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';

/** A line's concrete plan: purchase, transfer, or both. */
export type OptionAction = NonNullable<AssignmentLine['recommendedAction']>;

/**
 * The fields the designation and vendor-value rules actually read.
 *
 * Narrowed on purpose rather than taking a whole `AssignmentLine`: it documents
 * the real input, and it lets these rules be exercised directly without
 * fabricating every unrelated field of the wire shape.
 */
export type DesignatedLine = Pick<
  AssignmentLine,
  'recommendedAction' | 'topOption'
>;

/** {@link DesignatedLine} plus what cart membership is decided by. */
export type CartMemberLine = DesignatedLine &
  Pick<AssignmentLine, 'suggestionId' | 'status'>;

/**
 * The line's selected designation: its committed action when committed, else
 * the candidate top-option's action. Null when there's no plan at all.
 *
 * Lives here rather than beside the table columns because it is the input to
 * every vendor-value calculation below, and `cartLineColumns` already imports
 * from this module (so the dependency can only point this way).
 */
export function getLineDesignation(line: DesignatedLine): OptionAction | null {
  return line.recommendedAction ?? line.topOption?.action ?? null;
}

/** Whether the line puts anything on a purchase order. */
export function isBuyDesignated(line: DesignatedLine): boolean {
  const designation = getLineDesignation(line);
  return designation === 'buy' || designation === 'buy_and_transfer';
}

/** Whether the line moves any stock between warehouses. */
export function isTransferDesignated(line: DesignatedLine): boolean {
  const designation = getLineDesignation(line);
  return designation === 'transfer' || designation === 'buy_and_transfer';
}

/**
 * Vendor-order value of a set of lines.
 *
 * THE definition of "what this costs the vendor". A pure transfer contributes
 * nothing — no vendor, no spend — so it cannot count toward a vendor MOV or
 * freight threshold. Every surface that compares a cart against one of those
 * thresholds must come through here.
 */
export function sumPoValue(lines: DesignatedLine[]): number {
  return lines.reduce(
    (sum, line) =>
      sum + (isBuyDesignated(line) ? (line.topOption?.totalCost ?? 0) : 0),
    0,
  );
}

/** A cart's lines split by what a per-cart "Create orders" will actually mint. */
export interface CartPoLines<L> {
  /** Non-rejected lines the buyer still has selected. */
  selected: L[];
  /** Of those, the ones carrying a purchase. */
  buy: L[];
  /** Of those, the ones carrying a transfer. */
  transfer: L[];
  /** Vendor-order value of `buy`. What MOV and freight measure. */
  poValue: number;
}

/**
 * The single definition of "what this cart will actually buy".
 *
 * Shared because four surfaces derived it independently and two of them got it
 * wrong: the MOV/freight rail chip and the `below_mov` / `below_freight` chip
 * filters both summed `topOption.totalCost` over EVERY line in the cart, while
 * the create dialog and the table's cost column correctly ignore transfers. So
 * the rail counted transfer-only lines — which carry no vendor-order value at
 * all — plus deselected and rejected lines toward a VENDOR minimum. It reported
 * MOV met when the purchase alone was below it (the backend then refused the
 * create with `blockedBelowMov`), understated the "add $X to qualify" gap,
 * promised freight the order would not earn, and never moved when the buyer
 * changed the selection. On a transfer-dominated board the two numbers differ
 * by roughly the whole transfer side.
 */
export function selectCartPoLines<L extends CartMemberLine>(
  cartLines: L[],
  selectedIds: ReadonlySet<string>,
): CartPoLines<L> {
  const selected = cartLines.filter(
    (line) => line.status === 'pending' && selectedIds.has(line.suggestionId),
  );
  return {
    selected,
    buy: selected.filter(isBuyDesignated),
    transfer: selected.filter(isTransferDesignated),
    poValue: sumPoValue(selected),
  };
}

/**
 * A cart's vendor-order value, through the one definition
 * ({@link selectCartPoLines}) that the rail gauge and the Create summary also
 * use. The single place the "no selection state" fallback is written down, so a
 * caller cannot invent a different one.
 *
 * `selectedIds` omitted means "this caller has no selection state", NOT "nothing
 * is selected" — every non-rejected line counts. Only `SuggestionDetailModal`
 * is in that position: it reads lines from its own sibling query and computes
 * prev/next ORDER, and selection lives in `AssignmentBoard`'s local state.
 * Defaulting to an empty set instead would silently value every cart at zero.
 */
export function cartPoValue<L extends CartMemberLine>(
  cartLines: L[],
  selectedIds?: ReadonlySet<string>,
): number {
  const ids =
    selectedIds ?? new Set(cartLines.map((line) => line.suggestionId));
  return selectCartPoLines(cartLines, ids).poValue;
}

export type ChipKey =
  'sos' | 'potf' | 'below_mov' | 'below_freight' | 'deficits' | 'core';

export const CHIP_KEYS: readonly ChipKey[] = [
  'sos',
  'potf',
  'below_mov',
  'below_freight',
  'deficits',
  'core',
];

/**
 * POTF / non-stock predicate. Mirrors the supplier-list pill definition in
 * `backend/src/core/services/procurement/replenishmentTier1.ts` — a line
 * counts as POTF when its product+warehouse has `popularity_code` of `N`,
 * blank, or missing (no policy row). Rexel uses "POTF" and "non-stock"
 * interchangeably; keeping the label consistent across pill / chip / badge.
 */
export function isPotfLine(line: AssignmentLine): boolean {
  const pop = line.popularityCode;
  return pop == null || pop === '' || pop === 'N';
}

export function parseChipString(raw: string | undefined | null): Set<ChipKey> {
  const out = new Set<ChipKey>();
  if (!raw) return out;
  for (const tok of raw.split(',').map((t) => t.trim())) {
    if ((CHIP_KEYS as readonly string[]).includes(tok)) {
      out.add(tok as ChipKey);
    }
  }
  return out;
}

export function serializeChips(chips: Set<ChipKey>): string | undefined {
  if (chips.size === 0) return undefined;
  // Preserve a stable order so the URL roundtrips cleanly.
  return CHIP_KEYS.filter((k) => chips.has(k)).join(',');
}

/**
 * MOV/freight predicates evaluate against the **full** cart (not the chip- or
 * search-filtered subset) — otherwise filtering would mask the very threshold
 * the buyer cares about.
 *
 * The value itself goes through {@link selectCartPoLines}, the same helper the
 * rail gauge and the Create summary use, so the `<MOV` chip and the rail cannot
 * disagree about whether a cart is short. They did: this counted every line in
 * the cart, so the chip could list a cart the rail reported as MOV-met, and hide
 * one the rail reported as short.
 *
 * `selectedIds` is optional because only the board has selection state. Passing
 * it (the board does) measures what Create would actually mint. Omitting it —
 * `SuggestionDetailModal`, which reads lines from its own sibling query and
 * computes prev/next ORDER rather than a threshold readout — treats every
 * non-rejected line as in play, which is this function's long-standing
 * behaviour and the only basis available there.
 */
function cartIdsBelowThreshold(
  lines: AssignmentLine[],
  carts: CartAssignment[],
  thresholdKey: 'movThreshold' | 'freightThreshold',
  selectedIds?: ReadonlySet<string>,
): Set<string> {
  const linesByCart = new Map<string, AssignmentLine[]>();
  for (const cart of carts) linesByCart.set(cart.shipToWarehouseId, []);
  for (const line of lines) {
    if (line.shipToWarehouseId == null) continue;
    linesByCart.get(line.shipToWarehouseId)?.push(line);
  }
  const out = new Set<string>();
  for (const cart of carts) {
    const threshold = cart[thresholdKey];
    if (threshold == null || threshold <= 0) continue;
    const cartLines = linesByCart.get(cart.shipToWarehouseId) ?? [];
    if (cartPoValue(cartLines, selectedIds) < threshold) {
      out.add(cart.shipToWarehouseId);
    }
  }
  return out;
}

export interface LineFilterInput {
  lines: AssignmentLine[];
  carts: CartAssignment[];
  search: string;
  chips: Set<ChipKey>;
  /**
   * The buyer's current selection, when the caller has it. Only the `below_mov`
   * / `below_freight` chips read it, and only to measure a cart the same way the
   * rail gauge and Create do — see {@link cartIdsBelowThreshold} for what
   * omitting it means.
   */
  selectedIds?: ReadonlySet<string>;
}

export function filterAssignmentLines(
  input: LineFilterInput,
): AssignmentLine[] {
  const { lines, carts, search, chips, selectedIds } = input;
  const q = search.trim().toLowerCase();
  const belowMov = chips.has('below_mov')
    ? cartIdsBelowThreshold(lines, carts, 'movThreshold', selectedIds)
    : null;
  const belowFreight = chips.has('below_freight')
    ? cartIdsBelowThreshold(lines, carts, 'freightThreshold', selectedIds)
    : null;

  return lines.filter((l) => {
    if (q) {
      const hit =
        l.productSku?.toLowerCase().includes(q) ||
        l.productName.toLowerCase().includes(q) ||
        l.productDescription?.toLowerCase().includes(q) ||
        l.destinationWarehouseName.toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (chips.has('sos') && l.trigger !== 'sales_order') return false;
    if (chips.has('potf') && !isPotfLine(l)) return false;
    if (chips.has('deficits') && l.trigger !== 'deficit') return false;
    // Core material: the item sits in a core-offer program at its destination.
    if (chips.has('core') && !l.coreOffer) return false;
    if (belowMov) {
      if (l.shipToWarehouseId == null || !belowMov.has(l.shipToWarehouseId)) {
        return false;
      }
    }
    if (belowFreight) {
      if (
        l.shipToWarehouseId == null ||
        !belowFreight.has(l.shipToWarehouseId)
      ) {
        return false;
      }
    }
    return true;
  });
}
